<!-- src/components/stock/StockInternalTransfersTable.vue -->
<template>
  <div class="card stock-list-card">
    <div class="card-content">
      <!-- ===================================================
           HEADER
           =================================================== -->
      <div class="core-table-header">
        <div class="core-table-header-main">
          <span class="card-title"> Internal Transfers </span>

          <p class="core-table-subtitle">
            {{ transfers.length }}
            transfer{{ transfers.length === 1 ? "" : "s" }}
          </p>
        </div>
      </div>

      <!-- ===================================================
           LOADING
           =================================================== -->
      <div v-if="loading" class="progress">
        <div class="indeterminate"></div>
      </div>

      <!-- ===================================================
           EMPTY
           =================================================== -->
      <div v-else-if="!transfers.length" class="core-empty-state">
        No internal transfers have been recorded for this venue.
      </div>

      <!-- ===================================================
           TABLE
           =================================================== -->
      <div v-else class="table-wrap">
        <table class="striped highlight core-table transfer-table">
          <thead>
            <tr>
              <th>Transfer Date</th>
              <th>Reference</th>
              <th>From</th>
              <th>To</th>
              <th>Movement</th>

              <th class="right-align">Lines</th>

              <th class="right-align">Stock Units</th>

              <th class="right-align">Value</th>

              <th>Status</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="transfer in transfers" :key="transfer.id">
              <!-- DATE -->
              <td>
                {{ formatDate(transfer.transfer_date) }}
              </td>

              <!-- REFERENCE -->
              <td class="core-reference-cell">
                {{ transfer.transfer_reference || "—" }}
              </td>

              <!-- FROM -->
              <td>
                {{ transfer.from_venue_name || transfer.from_venue_id || "—" }}
              </td>

              <!-- TO -->
              <td>
                {{ transfer.to_venue_name || transfer.to_venue_id || "—" }}
              </td>

              <!-- MOVEMENT -->
              <td>
                <span v-if="transfer.direction === 'out'" class="movement movement-out">
                  <i class="material-icons"> remove </i>

                  Stock Out
                </span>

                <span v-else class="movement movement-in">
                  <i class="material-icons"> add </i>

                  Stock In
                </span>
              </td>

              <!-- LINES -->
              <td class="right-align">
                {{ transfer.line_count || 0 }}
              </td>

              <!-- QUANTITY -->
              <td
                class="right-align quantity-cell"
                :class="
                  transfer.direction === 'out'
                    ? 'red-text text-darken-2'
                    : 'green-text text-darken-2'
                "
              >
                {{ signedQuantity(transfer) }}
              </td>

              <!-- VALUE -->
              <td class="right-align core-money-cell">
                {{ money(transfer.total_value) }}
              </td>

              <!-- STATUS -->
              <td>
                <span
                  v-if="transfer.included_in_count_id"
                  class="core-badge core-badge--success"
                >
                  Included in Count
                </span>

                <span v-else class="core-badge core-badge--warning"> Pending Count </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "StockInternalTransfersTable",

  props: {
    transfers: {
      type: Array,
      default: () => [],
    },

    loading: {
      type: Boolean,
      default: false,
    },
  },

  methods: {
    /* =====================================================
       NUMBER
       ===================================================== */

    number(value) {
      const n = Number(value);

      return Number.isFinite(n) ? n : 0;
    },

    /* =====================================================
       DATE
       ===================================================== */

    formatDate(value) {
      const clean = String(value ?? "")
        .trim()
        .slice(0, 10);

      if (!/^\d{4}-\d{2}-\d{2}$/.test(clean)) {
        return clean || "—";
      }

      const [year, month, day] = clean.split("-");

      return `${day}/${month}/${year}`;
    },

    /* =====================================================
       NUMBER FORMAT
       ===================================================== */

    formatNumber(value) {
      return this.number(value).toLocaleString("en-GB", {
        maximumFractionDigits: 2,
      });
    },

    /* =====================================================
       SIGNED MOVEMENT
       ===================================================== */

    signedQuantity(transfer) {
      const quantity = Math.abs(this.number(transfer.total_quantity));

      if (transfer.direction === "out") {
        return `-${this.formatNumber(quantity)}`;
      }

      return `+${this.formatNumber(quantity)}`;
    },

    /* =====================================================
       MONEY
       ===================================================== */

    money(value) {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
      }).format(this.number(value));
    },
  },
};
</script>

<style scoped>
.stock-list-card {
  width: 100%;
  margin: 0 0 18px 0;
  overflow: hidden;
}

.quantity-cell {
  font-weight: 600;
}

.movement {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
}

.movement i {
  font-size: 16px;
}

.movement-in {
  color: #2e7d32;
}

.movement-out {
  color: #c62828;
}
</style>
